import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/design-system/Button'
import { Skeleton } from '@/design-system/primitives'
import { useAdminQuery } from '@/features/admin/api'
import { VoteResultsPanel } from '@/features/admin/components/VoteResultsPanel'
import type { VoteResults } from '@/features/admin/types'

import { useContestBoard } from '../api'
import { VoteControlRow } from '../ContestControl'
import type { ConductorVote } from '../types'
import { LinkWithQr, SectionHead, candidateName, text, useCandidates, useQrTargets } from './shared'

/**
 * Tous les votes de la soirée, au même endroit : les scrutins libres, ceux de
 * chaque tour, les départages.
 *
 * Le compteur de bulletins et le nombre de votants se lisent d'un coup d'œil
 * pendant que la salle vote ; le dépouillement (les voix de chacun) reste
 * replié — il ne s'ouvre qu'à la demande, à l'abri des regards.
 */
export function VotesTab({
  eventId,
  can,
}: {
  eventId: string | undefined
  can: (permission: string) => boolean
}) {
  const { t, i18n } = useTranslation()
  const board = useContestBoard(eventId, true)
  const candidates = useCandidates(eventId, can('competition.candidate.read'))
  const qr = useQrTargets(eventId, can('event.event.read'))
  const canVote = can('voting.category.control')
  const locale = i18n.language

  if (board.isPending) return <Skeleton className="h-40 w-full" />
  if (board.isError || !board.data) {
    return <p className="text-sm text-state-danger">{t('state.errorTitle')}</p>
  }

  const { votes, rounds, contests } = board.data
  const free = votes.filter((vote) => !vote.competition_category_id && !vote.is_tiebreak)
  const nameOf = (id: string | null) => {
    const found = candidates.data?.find((entry) => entry.id === id)
    return found ? candidateName(found) : '—'
  }
  const roundName = (vote: ConductorVote) => {
    const round = rounds.find((entry) => entry.id === vote.competition_round_id)
    return round ? text(round.name_i18n, locale) : null
  }

  const voteTarget = qr.data?.find((target) => target.kind === 'vote')
  const resultsTarget = qr.data?.find((target) => target.key === 'page:results')

  const row = (vote: ConductorVote) => (
    <VoteControlRow key={vote.id} eventId={eventId} vote={vote} canVote={canVote}>
      {/* Le tour, quand le nom du vote ne le dit pas déjà. */}
      {roundName(vote) && roundName(vote) !== text(vote.name_i18n, locale) ? (
        <p className="mt-1 text-caption text-content-muted">
          {t('conductor.cockpit.votes.round', { round: roundName(vote) })}
        </p>
      ) : null}
      {can('voting.result.read') ? (
        <VoteDetails eventId={eventId} vote={vote} can={can} nameOf={nameOf} />
      ) : null}
    </VoteControlRow>
  )

  return (
    <div className="space-y-8">
      {/* Le lien du vote, à montrer à la salle : à l'écran, sur une affiche,
          ou le téléphone tendu à qui ne le trouve pas. */}
      {voteTarget || resultsTarget ? (
        <section className="space-y-3">
          <SectionHead
            title={t('conductor.cockpit.votes.linksTitle')}
            hint={t('conductor.cockpit.votes.linksHint')}
          />
          <div className="grid gap-3 md:grid-cols-2">
            {voteTarget ? (
              <LinkWithQr title={t('conductor.cockpit.votes.voteLink')} target={voteTarget} />
            ) : null}
            {resultsTarget ? (
              <LinkWithQr
                title={t('conductor.cockpit.votes.resultsLink')}
                hint={
                  resultsTarget.published === false
                    ? t('conductor.cockpit.votes.resultsHidden')
                    : undefined
                }
                target={resultsTarget}
              />
            ) : null}
          </div>
        </section>
      ) : null}

      {votes.length === 0 ? (
        <p className="text-content-secondary">{t('conductor.cockpit.votes.none')}</p>
      ) : null}

      {free.length > 0 ? (
        <section className="space-y-3">
          <SectionHead
            title={t('conductor.cockpit.votes.freeTitle')}
            hint={t('conductor.cockpit.votes.freeHint')}
          />
          <div className="space-y-3">{free.map(row)}</div>
        </section>
      ) : null}

      {contests.map((contest) => {
        const own = votes.filter((vote) => vote.competition_category_id === contest.id)
        if (own.length === 0) return null
        return (
          <section key={contest.id} className="space-y-3">
            <SectionHead
              title={text(contest.name_i18n, locale)}
              hint={t('conductor.cockpit.votes.contestHint')}
            />
            <div className="space-y-3">{own.map(row)}</div>
          </section>
        )
      })}
    </div>
  )
}

/**
 * Sous un vote : combien de personnes ont voté, et le dépouillement replié.
 *
 * Les votants distincts viennent du dépouillement (`voting.result.read`) ; on
 * ne lit que leur nombre ici, et on ne relit en boucle que les votes ouverts.
 */
function VoteDetails({
  eventId,
  vote,
  can,
  nameOf,
}: {
  eventId: string | undefined
  vote: ConductorVote
  can: (permission: string) => boolean
  nameOf: (candidateId: string | null) => string
}) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const results = useAdminQuery<VoteResults>(
    ['admin-vote-results', vote.id],
    `/admin/events/${eventId}/vote-categories/${vote.id}/results`,
    eventId,
    {
      enabled: vote.status !== 'closed' || vote.ballots > 0,
      refetchInterval: vote.status === 'open' ? 10_000 : false,
      retry: false,
    },
  )

  return (
    <div className="mt-3">
      {results.data ? (
        <p className="text-caption text-content-secondary">
          {t('conductor.cockpit.votes.voters', { count: results.data.distinct_voters })}
        </p>
      ) : null}
      {vote.ballots > 0 ? (
        <Button className="mt-2" size="sm" variant="ghost" onClick={() => setOpen((value) => !value)}>
          {open ? t('conductor.cockpit.votes.hideCount') : t('conductor.cockpit.votes.showCount')}
        </Button>
      ) : null}
      {open ? (
        <VoteResultsPanel eventId={eventId} can={can} categoryId={vote.id} nameOf={nameOf} />
      ) : null}
    </div>
  )
}
